import { isBnplKlarnaSalePayment } from '@/utils/paymentGatewayDisplay'

const SALE_SOURCE_I18N_KEYS = {
  pos: 'sales.saleSourcePos',
  terminal_feed: 'sales.saleSourceTerminalFeed'
}

const PAYMENT_METHOD_I18N_KEYS = {
  cash: 'sales.paymentCash',
  card: 'sales.paymentCard',
  card_online: 'invoices.recordPayment.methodLabels.card_online',
  klarna: 'sales.paymentKlarna',
  split: 'sales.paymentSplit',
  tab: 'sales.paymentTab',
  tab_bar: 'sales.paymentTabBar',
  tab_portal: 'sales.paymentTabPortal',
  check: 'sales.paymentCheck'
}

const PAYMENT_FILTER_I18N_KEYS = {
  '': 'sales.paymentAll',
  cash: 'sales.paymentCash',
  card: 'sales.paymentCard',
  klarna: 'sales.paymentKlarna',
  split: 'sales.paymentSplit',
  tab: 'sales.paymentTab',
  tab_house: 'sales.paymentTabHouse',
  tab_bar: 'sales.paymentTabBar',
  tab_portal: 'sales.paymentTabPortal',
  check: 'sales.paymentCheck',
  tab_payment: 'sales.tabPaymentReceipt.badge',
  tab_payment_bar: 'sales.paymentTabPaymentBar'
}

const PAYMENT_FILTER_ICONS = {
  '': 'apps',
  cash: 'payments',
  card: 'credit_card',
  klarna: 'account_balance_wallet',
  split: 'call_split',
  tab: 'receipt_long',
  tab_house: 'home',
  tab_bar: 'local_bar',
  tab_portal: 'public',
  check: 'fact_check',
  tab_payment: 'paid',
  tab_payment_bar: 'local_bar'
}

/** Values accepted by GET /api/sales?payment_method= and the sales list filter. */
export const SALE_PAYMENT_METHOD_FILTER_VALUES = [
  'cash',
  'card',
  'klarna',
  'split',
  'tab',
  'tab_house',
  'tab_bar',
  'tab_portal',
  'check',
  'tab_payment',
  'tab_payment_bar'
]

/** English fallbacks for reports, thermal receipts, and other non-i18n contexts. */
const PAYMENT_METHOD_LABELS_EN = {
  cash: 'Cash',
  card: 'Card',
  klarna: 'Klarna',
  split: 'Split',
  tab: 'Tab',
  tab_bar: 'Bar tab',
  tab_portal: 'Online tab',
  check: 'Check'
}

const PAYMENT_METHOD_COLORS = {
  cash: 'primary',
  card: 'secondary',
  card_online: 'secondary',
  split: 'accent',
  check: 'orange-8'
}

const TAB_STYLED_METHODS = new Set(['tab', 'tab_portal'])

function normalizePaymentMethod (method) {
  return String(method || '').trim().toLowerCase()
}

function titleCasePaymentMethod (method) {
  return normalizePaymentMethod(method)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * @param {string|null|undefined} purpose
 * @returns {boolean}
 */
export function isBarTabPurpose (purpose) {
  return String(purpose || '').toLowerCase() === 'bar'
}

/**
 * @param {object|null|undefined} row
 * @returns {string|null}
 */
export function resolveSaleTabPurpose (row) {
  if (!row) return null
  const purpose = row.tab_payment_context?.tab_purpose
    ?? row.tab_charge_context?.tab_purpose
    ?? null
  return purpose ? String(purpose) : null
}

/**
 * @param {string|null|undefined} method
 * @returns {boolean}
 */
export function usesCustomTabChipStyle (method) {
  return TAB_STYLED_METHODS.has(normalizePaymentMethod(method))
}

/**
 * @param {string|null|undefined} method
 * @param {string|null|undefined} tabPurpose
 * @returns {string[]}
 */
export function getPaymentMethodChipClasses (method, tabPurpose = null) {
  if (!usesCustomTabChipStyle(method)) return []
  return isBarTabPurpose(tabPurpose)
    ? ['chip--bar-tab-gradient']
    : ['chip--house-tab-sunset']
}

/**
 * @param {string|null|undefined} tabPurpose
 * @returns {string[]}
 */
export function getTabPaymentDetailChipClasses (tabPurpose = null) {
  return isBarTabPurpose(tabPurpose)
    ? ['chip--bar-tab-gradient-tint']
    : ['chip--house-tab-sunset-tint']
}

/**
 * @param {import('vue-i18n').Composer['t']} t
 * @param {string|null|undefined} tabPurpose
 * @returns {string}
 */
export function formatTabPaymentDetailLabel (t, tabPurpose = null) {
  if (isBarTabPurpose(tabPurpose)) {
    return t('sales.paymentTabPaymentBar')
  }
  return t('sales.tabPaymentReceipt.badge')
}

/**
 * @param {string|null|undefined} tabPurpose
 * @returns {string}
 */
export function getTabPaymentDetailIcon (tabPurpose = null) {
  return isBarTabPurpose(tabPurpose) ? 'local_bar' : 'receipt_long'
}

/**
 * @param {string} filterValue
 * @returns {string|null}
 */
export function getPaymentFilterIcon (filterValue) {
  return PAYMENT_FILTER_ICONS[filterValue] ?? null
}

/**
 * @param {string|null|undefined} method
 * @returns {string}
 */
export function formatPaymentMethodFallback (method, tabPurpose = null) {
  const normalized = normalizePaymentMethod(method)
  if (!normalized) return ''
  if (normalized === 'tab' && isBarTabPurpose(tabPurpose)) {
    return PAYMENT_METHOD_LABELS_EN.tab_bar
  }
  return PAYMENT_METHOD_LABELS_EN[normalized] || titleCasePaymentMethod(normalized)
}

/**
 * @param {import('vue-i18n').Composer['t']} t
 * @param {string|null|undefined} method
 * @param {{ tabPurpose?: string|null }} [options]
 * @returns {string}
 */
export function formatSalePaymentMethod (t, method, options = {}) {
  const normalized = normalizePaymentMethod(method)
  if (!normalized) return ''
  const tabPurpose = options.tabPurpose ?? null

  if (normalized === 'tab' && isBarTabPurpose(tabPurpose)) {
    return t(PAYMENT_METHOD_I18N_KEYS.tab_bar)
  }

  const key = PAYMENT_METHOD_I18N_KEYS[normalized]
  if (key && t) {
    return t(key)
  }
  return formatPaymentMethodFallback(method, tabPurpose)
}

/**
 * @param {string|null|undefined} method
 * @returns {string}
 */
export function getPaymentMethodColor (method) {
  return PAYMENT_METHOD_COLORS[normalizePaymentMethod(method)] || 'grey-7'
}

/**
 * @param {string|null|undefined} method
 * @param {string|null|undefined} tabPurpose
 * @returns {string|null}
 */
export function getPaymentMethodIcon (method, tabPurpose = null) {
  const normalized = normalizePaymentMethod(method)
  if (normalized === 'tab') {
    return isBarTabPurpose(tabPurpose) ? 'local_bar' : 'receipt_long'
  }
  const icons = {
    cash: 'payments',
    card: 'credit_card',
    card_online: 'credit_card',
    split: 'call_split',
    tab_portal: 'public',
    check: 'fact_check'
  }
  return icons[normalized] || null
}

/**
 * @param {import('vue-i18n').Composer['t']} t
 * @returns {Array<{ label: string, value: string, icon: string|null }>}
 */
export function salePaymentMethodFilterOptions (t) {
  return [
    { label: t(PAYMENT_FILTER_I18N_KEYS['']), value: '', icon: PAYMENT_FILTER_ICONS[''] },
    ...SALE_PAYMENT_METHOD_FILTER_VALUES.map((value) => ({
      label: t(PAYMENT_FILTER_I18N_KEYS[value]),
      value,
      icon: PAYMENT_FILTER_ICONS[value] ?? null
    }))
  ]
}

/**
 * Client-side matcher aligned with GET /api/sales?payment_method= semantics.
 *
 * @param {object|null|undefined} row
 * @param {string} filterValue
 * @returns {boolean}
 */
export function matchesSalePaymentMethodFilter (row, filterValue) {
  if (!filterValue) return true
  if (!row) return false

  const method = normalizePaymentMethod(row.payment_method)
  const tabPurpose = resolveSaleTabPurpose(row)

  if (filterValue === 'tab_payment') {
    return !!row.tab_payment_context?.is_tab_payment
  }

  if (filterValue === 'tab_payment_bar') {
    return !!row.tab_payment_context?.is_tab_payment && isBarTabPurpose(tabPurpose)
  }

  if (filterValue === 'tab_house') {
    return method === 'tab' && !isBarTabPurpose(tabPurpose)
  }

  if (filterValue === 'tab_bar') {
    return method === 'tab' && isBarTabPurpose(tabPurpose)
  }

  return method === filterValue
}

/**
 * @param {import('vue-i18n').Composer['t']} t
 * @param {string|null|undefined} source
 * @returns {string}
 */
export function formatSaleSource (t, source) {
  const key = SALE_SOURCE_I18N_KEYS[source]
  if (key) {
    return t(key)
  }
  return source ? String(source) : ''
}

/**
 * @param {object|null|undefined} payment
 * @returns {boolean}
 */
export function isCardLikePayment (payment) {
  if (!payment) return false
  if (isBnplKlarnaSalePayment(payment)) return false
  return (
    !!payment.payment_transaction
    || !!payment.payment_transaction_id
    || payment.payment_method === 'card'
    || ['card', 'gateway', 'terminal'].includes(String(payment.payment_method_type || '').toLowerCase())
  )
}

export function isKlarnaLikePayment (payment) {
  return isBnplKlarnaSalePayment(payment)
}

/**
 * @param {import('vue-i18n').Composer['t']} t
 * @param {object|null|undefined} payment
 * @returns {string}
 */
function resolveSplitLegRowLabel (payment, labelForCard, labelForCash, labelForPaymentNumber) {
  if (!payment || payment.payment_method !== 'split') return null
  const splitLabel = payment.payment_details?.split_leg_label
  if (typeof splitLabel === 'string' && splitLabel.trim() !== '') {
    return splitLabel.trim()
  }
  const legIndex = payment.payment_details?.split_leg_index
  if (typeof legIndex === 'number' && Number.isFinite(legIndex)) {
    return labelForPaymentNumber(legIndex + 1)
  }
  const type = String(payment.payment_method_type || '').toLowerCase()
  if (type === 'cash') return labelForCash
  if (isCardLikePayment(payment)) return labelForCard
  return null
}

export function formatSalePaymentRowLabel (t, payment) {
  if (!payment) return t('sales.paymentUnknown')
  const splitLeg = resolveSplitLegRowLabel(
    payment,
    t('sales.paymentCard'),
    t('sales.paymentCash'),
    (n) => t('payments.splitPaymentNumber', { n })
  )
  if (splitLeg) return splitLeg
  if (payment.payment_method === 'cash') return t('sales.paymentCash')
  if (isKlarnaLikePayment(payment)) return t('sales.paymentKlarna')
  if (isCardLikePayment(payment)) return t('sales.paymentCard')
  return formatSalePaymentMethod(t, payment.payment_method)
}

/**
 * English row labels for thermal receipts and other non-i18n contexts.
 * @param {object|null|undefined} payment
 * @returns {string}
 */
export function formatSalePaymentRowLabelEn (payment) {
  if (!payment) return 'Payment'
  const splitLeg = resolveSplitLegRowLabel(
    payment,
    PAYMENT_METHOD_LABELS_EN.card,
    PAYMENT_METHOD_LABELS_EN.cash,
    (n) => `Payment ${n}`
  )
  if (splitLeg) return splitLeg
  const method = normalizePaymentMethod(payment.payment_method)
  if (method === 'cash') return PAYMENT_METHOD_LABELS_EN.cash
  if (isKlarnaLikePayment(payment)) return PAYMENT_METHOD_LABELS_EN.klarna
  if (isCardLikePayment(payment)) return PAYMENT_METHOD_LABELS_EN.card
  return PAYMENT_METHOD_LABELS_EN[method] || titleCasePaymentMethod(payment.payment_method)
}

/**
 * Total charged to the customer's card for this payment row (goods + tip + convenience fee).
 * Prefer gateway-reported charged_total when present.
 *
 * @param {object|null|undefined} payment
 * @returns {number}
 */
export function getSalePaymentChargedTotal (payment) {
  if (!payment) return 0
  const details = payment.payment_details || {}
  const charged = Number(details.charged_total)
  if (Number.isFinite(charged) && charged > 0) {
    return charged
  }
  return (
    Number(payment.amount || 0)
    + Number(payment.tip_amount || 0)
    + Number(payment.fee_amount || 0)
  )
}

/**
 * @param {object|null|undefined} primaryCardPayment
 * @returns {boolean}
 */
/**
 * @param {object|null|undefined} primaryCardPayment
 * @param {object|null|undefined} [sale]
 * @returns {boolean}
 */
export function shouldShowCardPaymentDetails (primaryCardPayment, sale = null) {
  if (sale && normalizePaymentMethod(sale.payment_method) === 'split') {
    return false
  }
  return !!primaryCardPayment && isCardLikePayment(primaryCardPayment)
}

const SALE_STATUS_I18N_KEYS = {
  pending: 'sales.saleStatusPending',
  completed: 'sales.saleStatusCompleted',
  void: 'sales.saleStatusVoid',
  refunded: 'sales.saleStatusRefunded'
}

/**
 * @param {import('vue-i18n').Composer['t']} t
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function formatSaleStatus (t, status) {
  const normalized = String(status || '').toLowerCase()
  const key = SALE_STATUS_I18N_KEYS[normalized]
  if (key) {
    return t(key)
  }
  if (normalized) {
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
  }
  return t('sales.saleStatusUnknown')
}

/**
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function getSaleStatusColor (status) {
  const colors = {
    completed: 'positive',
    pending: 'warning',
    void: 'negative',
    refunded: 'grey'
  }
  return colors[String(status || '').toLowerCase()] || 'grey'
}
